import TokenResponse from 'owa-service/lib/contract/TokenResponse';
import tokenRequest from 'owa-service/lib/factory/tokenRequest';
import RequestOptions from 'owa-service/lib/RequestOptions';
import sleep from 'owa-sleep';
import getAccessTokenforResourceOperation from 'owa-service/lib/operation/getAccessTokenforResourceOperation';
import { getUserToken } from './utils/userTokenHandler';
import { getOrigin } from 'owa-url/lib/getOrigin';
import TokenResponseCode from 'owa-service/lib/contract/TokenResponseCode';

export const SCENARIO_NAME = 'OWA_GetAccessTokenForResource';

export default async function getAccessTokenforResourceFromServer(
    resource: string,
    retryCount: number,
    requestId: string
): Promise<TokenResponse> {
    const userToken = await getUserToken();
    // In OPX scenario, return the token retrived from host app instead of doing OBO flow
    if (userToken && resource == getOrigin()) {
        return { AccessToken: userToken };
    }

    const options: RequestOptions = {
        isUserActivity: false,
        datapoint: {
            customData: {
                Scenario: SCENARIO_NAME,
                Resource_Url: resource,
                Retry_attempt: retryCount,
                Request_Id: requestId,
            },
            headersCustomData: (headers: Headers) => {
                return {
                    TokenResultDetails: headers.get('x-tokenresult') || '',
                };
            },
            jsonCustomData: (tokenResponse: TokenResponse) => {
                return {
                    TokenStatus:
                        tokenResponse?.TokenResultCode?.toString() || TokenResponseCode.Failed,
                };
            },
        },
        returnFullResponseOnSuccess: true,
    };

    const response = await (getAccessTokenforResourceOperation(
        tokenRequest({ Resource: resource, UserToken: userToken }),
        options
    ) as Promise<Response>);

    return (await response.json()) as TokenResponse;
}

/**
 * Adds a retry with timeout when fetching Access Token from server
 * @param resource - the resource string
 * @param retryCount - the retry count
 * @param retryDelay - time between 2 retries
 * @param requestId - the requestId
 * @returns - TokenResponse wrapped around in a Promise
 */
export async function getAccessTokenforResourceFromServerWithRetry(
    resource: string,
    retryCount: number,
    retryDelay: number,
    requestId: string
): Promise<TokenResponse> {
    if (retryCount < 1) {
        return Promise.reject();
    }

    return getAccessTokenforResourceFromServer(resource, retryCount, requestId).catch(error =>
        sleep(retryDelay).then(() => {
            return getAccessTokenforResourceFromServerWithRetry(
                resource,
                retryCount - 1,
                retryDelay * 2,
                requestId
            );
        })
    );
}
